/**
 * queueRecovery — getting a FAILED entry back into the flush lane, automatically,
 * once per app load. The "how a stranded write is rescued without a human tap" layer.
 *
 * WHY THIS EXISTS (BUG-FE-QUEUE-FAILED-NEVER-REPLAYS): `flushQueue` opens each entry
 * with `if (entry.status !== 'pending') continue`. An entry that exhausted its retries
 * is 'failed' — so it is skipped by every subsequent flush, forever. The only escapes
 * are queueExit's `retryFailedEntries` / `discardEntry`, BOTH reachable solely by a
 * user tap on the offline surface. A real rep therefore carried 13 days of visit
 * reports — ~84 of them — alive in his phone's IndexedDB, recoverable, and unreachable.
 * A PWA reinstall would have destroyed them for real. Curing the submit path
 * (BUG-2026-07-14-VC559) fixes the NEXT report; it does nothing for the backlog already
 * stranded on the device. This module is what brings that backlog home.
 *
 * THE SYSTEM CAUSE (Block A, three questions): the absent mechanism was not "a retry"
 * — VC559 already built one. It was that nothing ever re-examined a terminal state
 * WITHOUT a human. Every door out of 'failed' required the rep to notice a banner and
 * tap it. A durable queue whose only exit is a tap is a data-loss device with extra
 * steps, because the rep whose data is stranded is exactly the rep who was never told.
 *
 * THE ANTI-STORM RULE: recovery is a scalpel, not a blanket retry. A field rep's
 * battery and data plan are real constraints, and re-pending a deterministic failure
 * re-pins it on every load forever — which is why the original retry was manual. The
 * judgement of what is worth re-pending is NOT made here: it is `isRetryableReason`
 * (queueStore), the SAME classifier the manual tap consults. One classifier, two
 * callers — see its docstring for why a second copy would be the bug.
 *
 * LAYERING: this sits at LAYER 1.5 — it imports only queueStore / queueStoreWrite (LAYER 1) and takes
 * `flushQueue` as a PARAMETER. queueFlush (LAYER 2) drives it from `startOfflineQueue`,
 * so importing `flushQueue` here would close the cycle
 * queueFlush → queueRecovery → queueFlush, which the repo's `check:circular` (madge)
 * gate rejects — and which is a real init-order hazard, not just a lint. The parameter
 * IS the seam that keeps the dependency arrow one-way.
 */

import { isGenuinelyFailed, isRetryableReason } from './queueStore'
import { updateQueue } from './queueStoreWrite'
import { rePendDependentsBehindPendingPredecessors } from './queueOrdering'
import type { QueueEntry, FlushResult } from './offlineQueueTypes'

/** What a recovery cycle did. Every field is load-bearing — see runQueueRecovery. */
export interface QueueRecoveryResult {
  /** Entries moved failed → pending by this cycle. */
  rePended: number
  /**
   * Failed entries deliberately left alone: a retry cannot change their fate
   * (deterministic 4xx / conflict). Recovering NOTHING because everything stranded is
   * deterministic is a different outcome from recovering nothing because the queue was
   * empty, and a caller must be able to tell them apart (D-33 R1).
   */
  skippedDeterministic: number
  /** The flush this cycle ran. Always present — the app-load flush runs regardless. */
  flush: FlushResult
}

/** The counts half of a cycle, before the flush. */
export type QueueRePendCounts = Omit<QueueRecoveryResult, 'flush'>

/**
 * Move every RECOVERABLE failed entry back to pending, IN PLACE, and report what
 * happened. Pure apart from the mutation of the entries it is handed: no IO, no clock,
 * no network — which is what makes the anti-storm policy testable without a database.
 *
 * NEVER deletes, purges or reorders. Recovery only.
 *
 * A LEGACY entry (no `failureReason` — written before VC559 tagged them, i.e. exactly
 * the rep's stranded backlog) is treated as retryable by `isRetryableReason`, so it
 * gets an attempt. That attempt is SELF-BOUNDING and needs no bookkeeping: re-pending
 * sets status='pending', and the entry can only return to 'failed' THROUGH
 * `applyReplayFailure`, which always stamps a reason on the way. So an untagged entry
 * is re-pended at most once — the next load sees a classified entry and applies the
 * rules above permanently. That is why there is no "already attempted" flag here.
 */
export function rePendRecoverableEntries(queue: QueueEntry[]): QueueRePendCounts {
  const counts: QueueRePendCounts = { rePended: 0, skippedDeterministic: 0 }

  for (const entry of queue) {
    if (!isGenuinelyFailed(entry)) continue

    if (!isRetryableReason(entry.failureReason)) {
      // Deterministic → refused, on every load, forever. The rep's manual retry (which
      // implies "I corrected the input") remains the only door, by design.
      counts.skippedDeterministic++
      continue
    }

    entry.status = 'pending'
    entry.retries = 0
    delete entry.failureReason
    counts.rePended++
  }

  // A dependent pinned behind a dead predecessor (queueOrdering) is not judged on its
  // own reason: it was counted as skipped above and follows its predecessor back into
  // the flush lane only when that predecessor was just re-pended.
  const dependentsMoved = rePendDependentsBehindPendingPredecessors(queue)
  counts.rePended += dependentsMoved
  counts.skippedDeterministic -= dependentsMoved

  return counts
}

/**
 * ONE recovery cycle: re-pend what is recoverable, persist it, then flush.
 *
 * Called once per app load from `startOfflineQueue` — never on the 30s interval, never
 * in a loop. `startOfflineQueue` is already idempotent (its `flushTimer` guard returns
 * early on a second call), so both live call sites (AppLayout + useMorningSync) inherit
 * the once-per-load guarantee rather than each needing to remember it.
 *
 * The flush runs whether or not anything was re-pended, because it is ALSO the queue's
 * ordinary init flush for entries that are already pending; skipping it would strand
 * those instead — trading one stranded set for another.
 *
 * D-33 R1 — the returned result says WHAT happened. `flushQueue` alone returns a bare
 * `{synced: 0, failed: 0}` for "flushed nothing" and (with `skipped`) for "refused to
 * run"; neither can express "there were 84 stranded reports and I brought them back".
 * A caller here can always tell a cycle that recovered N from one that recovered
 * nothing, and why it recovered nothing.
 *
 * @param flushQueue injected by queueFlush — see the LAYERING note in the file header.
 */
export async function runQueueRecovery(
  flushQueue: () => Promise<FlushResult>,
): Promise<QueueRecoveryResult> {
  // Re-pend on the CURRENT queue inside the serialised chain (queueStoreWrite) and persist
  // BEFORE flushing — the flush reads the queue back off disk, and must see the re-pended
  // entries. Only write when something actually changed. The flush runs after the step
  // settles: calling it inside the step would wait on itself.
  const counts = await updateQueue(queue => {
    const result = rePendRecoverableEntries(queue)
    return result.rePended > 0 ? { next: queue, result } : { unchanged: true, result }
  })
  const flush = await flushQueue()
  return { ...counts, flush }
}
